/**
 * war_sky_feed.ts — the ground's ONE reading of the fleet war (space campaign slice L, 2026-10-01; report §10.4).
 *
 * The server writes one ≤ 1 KiB summary per war cycle (`campaign_sky`, lib/war_sky_ops on the server — the counts the
 * campaign keeps, never a second computation). The ground subscribes that one small table and reads THIS cycle's row:
 * whether a war exists (a ceasefire row says no), when it ends (the row's own end — a retreat's or the resolution's), the
 * physical fort, the strategic front, and how many capitals, escorts and gunships still stand on each side. It replaces
 * the sky's old reduction of `civ_treaty` + `space_campaign` (kept as the lever-off path in SkyModule).
 *
 * A probe override (`__warSky.override(summary | null)`) lets a picture pin counts without a live war.
 */
import type { DbConnection } from '../module_bindings/index.js';
import type { WarSky } from '@shared/war_sky.js';
import type { BattleSkyInputs } from './night_sky_schedule.js';

type SkyRowLike = {
  cyc: bigint; campaignId: bigint; state: string; fort: string; front: string;
  openedAt: { microsSinceUnixEpoch: bigint }; endsAt: { microsSinceUnixEpoch: bigint };
  capsA: number; capsB: number; capsComA: number; capsComB: number; frigA: number; frigB: number; corvA: number; corvB: number;
  gunA: number; gunB: number; retreatSide: number; winner: string; validated: boolean; rounds: number; seq: number;
};

/** The stored row as the pure record (the mirror's shape). */
export function warSkyFromRow(r: SkyRowLike): WarSky {
  return {
    cyc: Number(r.cyc), campaignId: Number(r.campaignId), state: r.state as WarSky['state'], fort: r.fort, front: r.front,
    openedAtS: Number(r.openedAt.microsSinceUnixEpoch / 1_000_000n), endsAtS: Number(r.endsAt.microsSinceUnixEpoch / 1_000_000n),
    caps: [r.capsA, r.capsB], capsCommitted: [r.capsComA, r.capsComB], frigates: [r.frigA, r.frigB],
    corvettes: [r.corvA, r.corvB], gunships: [r.gunA, r.gunB],
    retreatSide: r.retreatSide, winner: r.winner, validated: r.validated, rounds: r.rounds,
  };
}

/** What the sky needs from a summary (pure): a ceasefire hour is no war; any other row ends the war at ITS end. */
export function battleInputsFromWarSky(s: WarSky | null): BattleSkyInputs {
  if (!s) return { ceasefire: false, campaign: null };
  if (s.state === 'ceasefire') return { ceasefire: true, campaign: null };
  return { ceasefire: false, campaign: { state: s.state, endsAtS: s.endsAtS } };
}

let subbed = false;
let readAt = -1;
let cached: WarSky | null = null;
let rows = 0;
let override: WarSky | null | undefined;

/** THIS cycle's summary (cached a second), or null. Subscribes the one table on first use. */
export function warSkyNow(conn: DbConnection | null, nowS: number, cycOf: (s: number) => number): WarSky | null {
  if (override !== undefined) return override;
  if (!conn) return cached;
  if (!subbed) {
    subbed = true;
    try { conn.subscriptionBuilder().subscribe(['SELECT * FROM campaign_sky']); } catch { /* pre-publish */ }
  }
  if (nowS - readAt < 1 && readAt >= 0) return cached;
  readAt = nowS;
  try {
    const cyc = cycOf(nowS);
    const tbl = (conn.db as unknown as { campaignSky?: { iter(): Iterable<SkyRowLike> } }).campaignSky;
    if (!tbl) return cached;
    let found: SkyRowLike | null = null;
    let n = 0;
    for (const r of tbl.iter()) { n++; if (Number(r.cyc) === cyc) found = r; }
    rows = n;
    cached = found ? warSkyFromRow(found) : null;
  } catch { /* rows mid-churn — keep the last reading */ }
  return cached;
}

/** The feed's telemetry (`__nightSky` / the probe). */
export function warSkyFeedState(): { subbed: boolean; rows: number; overridden: boolean; summary: WarSky | null } {
  return { subbed, rows, overridden: override !== undefined, summary: override !== undefined ? override : cached };
}

/** Probe lever: pin a summary (or null = "no war this cycle"); `undefined` releases it. */
export function overrideWarSky(s: WarSky | null | undefined): void { override = s; readAt = -1; }

/** Harnesses: forget the subscription / cache (a fresh connection). */
export function resetWarSkyFeed(): void { subbed = false; readAt = -1; cached = null; rows = 0; override = undefined; }
